import { sanitizeTagModifier, formatDictionaryTemplate } from "./timeline-utils";

type TimelineFilterLabels = {
	all?: string;
	filters?: string;
	removeFilter?: string;
};

function getOptionLabel(option: HTMLButtonElement): string {
	return (
		option.querySelector<HTMLElement>(".timeline-filter__option-label")?.textContent?.trim() ??
		option.textContent?.trim() ??
		""
	);
}

function getCategoryOption(
	options: HTMLButtonElement[],
	categoryKey: string
): HTMLButtonElement | undefined {
	return options.find((option) => option.dataset.categoryKey === categoryKey);
}

function syncOptionStates(options: HTMLButtonElement[], activeCategoryKeys: string[]): void {
	options.forEach((option) => {
		const categoryKey = option.dataset.categoryKey ?? "";
		const isAll = categoryKey === "";
		const isActive = isAll
			? activeCategoryKeys.length === 0
			: activeCategoryKeys.includes(categoryKey);

		option.classList.toggle("timeline-filter__option--active", isActive);
		option.setAttribute("aria-selected", isActive ? "true" : "false");
	});
}

function renderSelectedChips(
	selectedHost: HTMLElement,
	options: HTMLButtonElement[],
	activeCategoryKeys: string[],
	removeFilterTemplate: string,
	onRemove: (categoryKey: string) => void
): () => void {
	const chipHandlers: Array<{ chip: HTMLButtonElement; handler: () => void }> = [];

	selectedHost.replaceChildren();

	if (activeCategoryKeys.length === 0) {
		selectedHost.hidden = true;
		return () => undefined;
	}

	selectedHost.hidden = false;

	activeCategoryKeys.forEach((categoryKey) => {
		const option = getCategoryOption(options, categoryKey);
		if (!option) {
			return;
		}

		const label = getOptionLabel(option);
		const modifier = sanitizeTagModifier(categoryKey);
		const chip = document.createElement("button");
		chip.type = "button";
		chip.className = `timeline-filter__chip timeline-filter__chip--${modifier}`;
		chip.dataset.categoryKey = categoryKey;
		chip.setAttribute("role", "listitem");
		chip.setAttribute("aria-label", formatDictionaryTemplate(removeFilterTemplate, label));
		chip.innerHTML = `
			<span class="timeline-filter__chip-label">${label}</span>
			<span class="timeline-filter__chip-remove" aria-hidden="true"></span>
		`;

		const handler = () => onRemove(categoryKey);
		chip.addEventListener("click", handler);
		chipHandlers.push({ chip, handler });
		selectedHost.appendChild(chip);
	});

	return () => {
		chipHandlers.forEach(({ chip, handler }) => chip.removeEventListener("click", handler));
	};
}

function getPanelOptions(panel: HTMLElement): HTMLButtonElement[] {
	return Array.from(panel.querySelectorAll<HTMLButtonElement>(".timeline-filter__option"));
}

function focusMenuOption(options: HTMLButtonElement[], index: number): void {
	if (options.length === 0) {
		return;
	}

	const nextIndex = (index + options.length) % options.length;
	options[nextIndex]?.focus();
}

function getFocusedOptionIndex(options: HTMLButtonElement[]): number {
	const activeElement = document.activeElement;
	return options.findIndex((option) => option === activeElement);
}

export function bindTimelineFilter(
	root: HTMLElement,
	labels: TimelineFilterLabels,
	onChange: (activeCategoryKeys: string[]) => void | Promise<void>
): {
	destroy: () => void;
	reset: () => void;
	setCategoryKeys: (categoryKeys: string[], notifyChange?: boolean) => void;
	getCategoryKeys: () => string[];
	getAvailableCategoryKeys: () => string[];
} {
	const control = root.querySelector<HTMLElement>("[data-timeline-filter]");
	const trigger = root.querySelector<HTMLButtonElement>(".timeline-filter__trigger");
	const panel = root.querySelector<HTMLElement>(".timeline-filter__panel");
	const selectedHost = root.querySelector<HTMLElement>("[data-timeline-filter-selected]");
	const triggerLabel = root.querySelector<HTMLElement>(".timeline-filter__trigger-label");
	const options = Array.from(root.querySelectorAll<HTMLButtonElement>(".timeline-filter__option"));

	if (!control || !trigger || !panel || !triggerLabel || !selectedHost || options.length === 0) {
		return {
			destroy: () => undefined,
			reset: () => undefined,
			setCategoryKeys: () => undefined,
			getCategoryKeys: () => [],
			getAvailableCategoryKeys: () => [],
		};
	}

	if (!selectedHost.hasAttribute("role")) {
		selectedHost.setAttribute("role", "list");
	}

	const allLabel = labels.all ?? "All";
	const filtersLabel = labels.filters ?? "Filters";
	const removeFilterTemplate = labels.removeFilter ?? "Remove {0}";

	options.forEach((option) => {
		const categoryKey = option.dataset.categoryKey ?? "";
		const label = getOptionLabel(option);

		option.classList.add(
			categoryKey
				? `timeline-filter__option--${sanitizeTagModifier(categoryKey)}`
				: "timeline-filter__option--all"
		);

		option.innerHTML = categoryKey
			? `<span class="timeline-filter__option-label">${label}</span><span class="timeline-filter__option-dot" aria-hidden="true"></span>`
			: `<span class="timeline-filter__option-label">${label}</span>`;
	});

	let activeCategoryKeys: string[] = [];
	let isOpen = false;
	let cleanupChipHandlers: () => void = () => undefined;

	const applyFilterState = () => {
		syncOptionStates(options, activeCategoryKeys);
		triggerLabel.textContent = activeCategoryKeys.length === 0
			? `${filtersLabel}: ${allLabel}`
			: filtersLabel;
		cleanupChipHandlers();
		cleanupChipHandlers = renderSelectedChips(
			selectedHost,
			options,
			activeCategoryKeys,
			removeFilterTemplate,
			(categoryKey) => {
				activeCategoryKeys = activeCategoryKeys.filter((key) => key !== categoryKey);
				applyFilterState();
				void onChange([...activeCategoryKeys]);
			}
		);
	};

	let panelCloseTimer: ReturnType<typeof setTimeout> | null = null;

	const clearPanelCloseTimer = () => {
		if (panelCloseTimer === null) {
			return;
		}

		clearTimeout(panelCloseTimer);
		panelCloseTimer = null;
	};

	const setOpen = (nextOpen: boolean) => {
		if (nextOpen === isOpen) {
			return;
		}

		isOpen = nextOpen;
		clearPanelCloseTimer();
		trigger.setAttribute("aria-expanded", nextOpen ? "true" : "false");

		if (nextOpen) {
			control.classList.remove("is-open");
			panel.hidden = false;
			void panel.offsetHeight;

			requestAnimationFrame(() => {
				if (!isOpen) {
					return;
				}

				control.classList.add("is-open");

				const panelOptions = getPanelOptions(panel);
				const activeOption = panelOptions.find((option) => option.classList.contains("timeline-filter__option--active"));
				(activeOption ?? panelOptions[0])?.focus();
			});
			return;
		}

		control.classList.remove("is-open");

		panelCloseTimer = setTimeout(() => {
			panelCloseTimer = null;

			if (!isOpen) {
				panel.hidden = true;
			}
		}, 700);
	};

	const toggleCategory = (categoryKey: string) => {
		if (!categoryKey) {
			activeCategoryKeys = [];
		} else {
			activeCategoryKeys = activeCategoryKeys.includes(categoryKey)
				? activeCategoryKeys.filter((key) => key !== categoryKey)
				: [...activeCategoryKeys, categoryKey];
		}

		applyFilterState();
		void onChange([...activeCategoryKeys]);
		setOpen(false);
		trigger.focus();
	};

	const handleDocumentClick = (event: MouseEvent) => {
		const target = event.target;
		if (!(target instanceof Node) || root.contains(target)) {
			return;
		}

		setOpen(false);
	};

	const handleDocumentKeyDown = (event: KeyboardEvent) => {
		if (!isOpen || event.key !== "Escape") {
			return;
		}

		event.preventDefault();
		setOpen(false);
		trigger.focus();
	};

	const handlePanelKeyDown = (event: KeyboardEvent) => {
		if (!isOpen) {
			return;
		}

		const panelOptions = getPanelOptions(panel);
		const focusedIndex = getFocusedOptionIndex(panelOptions);

		if (event.key === "ArrowDown") {
			event.preventDefault();
			focusMenuOption(panelOptions, focusedIndex + 1);
			return;
		}

		if (event.key === "ArrowUp") {
			event.preventDefault();
			focusMenuOption(panelOptions, focusedIndex - 1);
			return;
		}

		if (event.key === "Home") {
			event.preventDefault();
			panelOptions[0]?.focus();
			return;
		}

		if (event.key === "End") {
			event.preventDefault();
			panelOptions[panelOptions.length - 1]?.focus();
		}
	};

	const handleTriggerKeyDown = (event: KeyboardEvent) => {
		if (isOpen) {
			return;
		}

		if (event.key === "ArrowDown" || event.key === "Enter" || event.key === " ") {
			event.preventDefault();
			setOpen(true);
		}
	};

	trigger.addEventListener("click", () => {
		setOpen(!isOpen);
	});

	trigger.addEventListener("keydown", handleTriggerKeyDown);
	panel.addEventListener("keydown", handlePanelKeyDown);
	document.addEventListener("click", handleDocumentClick);
	document.addEventListener("keydown", handleDocumentKeyDown);

	const optionHandlers = options.map((option) => {
		const handler = (event: Event) => {
			event.preventDefault();
			toggleCategory(option.dataset.categoryKey ?? "");
		};

		option.addEventListener("click", handler);
		return { option, handler };
	});

	applyFilterState();

	const reset = () => {
		if (activeCategoryKeys.length === 0) {
			return;
		}

		activeCategoryKeys = [];
		applyFilterState();
		void onChange([]);
	};

	const setCategoryKeys = (categoryKeys: string[], notifyChange = true) => {
		activeCategoryKeys = [...categoryKeys];
		applyFilterState();

		if (notifyChange) {
			void onChange([...activeCategoryKeys]);
		}
	};

	const getCategoryKeys = () => [...activeCategoryKeys];

	const getAvailableCategoryKeys = () =>
		options
			.map((option) => option.dataset.categoryKey ?? "")
			.filter(Boolean);

	return {
		destroy: () => {
			document.removeEventListener("click", handleDocumentClick);
			document.removeEventListener("keydown", handleDocumentKeyDown);
			trigger.removeEventListener("keydown", handleTriggerKeyDown);
			panel.removeEventListener("keydown", handlePanelKeyDown);
			optionHandlers.forEach(({ option, handler }) => option.removeEventListener("click", handler));
			cleanupChipHandlers();
			clearPanelCloseTimer();
			setOpen(false);
			panel.hidden = true;
		},
		reset,
		setCategoryKeys,
		getCategoryKeys,
		getAvailableCategoryKeys,
	};
}
